import { loadStripe } from '@stripe/stripe-js';
import appConfig from '@/utils/config';
import { getUserLang } from '@/i18n/utils';
import { NationsLab } from '@/utils/locations';

// 获取当前语言并映射到 Stripe 支持的语言代码
export const getCurrentLocale = () => {
  const lang = getUserLang().detectedValue;
  const localeMap = {
    'en-US': 'en',
    'pt-BR': 'pt-BR',
    'ja-JP': 'ja'
  } as const;
  return localeMap[lang as keyof typeof localeMap] || 'en';
};

export const stripePromise = loadStripe(appConfig['stripe_public_key'], {
  locale: getCurrentLocale()
});

// RFC 完整验证函数
export const validateRFC = (value: string): boolean => {
  // 1. 长度验证
  if (value.length !== 13) {
    return false;
  }

  // 2. 前4位：仅字母（A-Z + 带重音的西班牙语大写字母）
  const first4 = value.substring(0, 4);
  // A-Z (U+0041-U+005A) + Latin-1 Supplement (U+00C0-U+00FF) 包含带重音的字母
  if (!/^[A-Z\u00C0-\u00FF]{4}$/.test(first4)) {
    return false;
  }

  // 3. 中间6位：日期 YYMMDD
  const dateStr = value.substring(4, 10);
  if (!/^\d{6}$/.test(dateStr)) {
    return false;
  }

  // 解析日期
  const yy = parseInt(dateStr.substring(0, 2));
  const mm = parseInt(dateStr.substring(2, 4));
  const dd = parseInt(dateStr.substring(4, 6));

  // 验证年月日范围
  if (mm < 1 || mm > 12) {
    return false;
  }

  // 年份转换和验证
  // RFC 年份解读：
  // - YY 50-99 -> 1950-1999
  // - YY 00-当前年份后两位 -> 2000-当前年
  const currentYear = new Date().getFullYear();
  const currentYearLastTwo = currentYear % 100;
  let fullYear;

  if (yy >= 50 && yy <= 99) {
    fullYear = 1900 + yy; // 1950-1999
  } else if (yy >= 0 && yy <= currentYearLastTwo) {
    fullYear = 2000 + yy; // 2000-当前年
  } else {
    return false; // 年份超出范围
  }

  // RFC 最早日期：1950-01-01
  if (fullYear < 1950) {
    return false;
  }

  // 验证日期是否有效
  const daysInMonth = new Date(fullYear, mm, 0).getDate();
  if (dd < 1 || dd > daysInMonth) {
    return false;
  }

  // 验证日期不能超过今天
  const date = new Date(fullYear, mm - 1, dd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) {
    return false;
  }

  // 4. 后3位：字母或数字
  const last3 = value.substring(10, 13);
  if (!/^[A-Z0-9]{3}$/.test(last3)) {
    return false;
  }

  return true;
};

// Prevent PO Box / PMB / Post Office Box in shipping address
export const containsProhibitedPOBox = (input: string): boolean => {
  const text = input || '';
  // Matches: P.O. Box / POBOX / P O Box / P.O. B.O.X (spaces and punctuation tolerant)
  const poBoxRegex = /P\s*\.?\s*O\s*\.?\s*B\s*\.?\s*O\s*\.?\s*X/i;
  // Matches: Post Office Box / Post.Office.Box (full phrase, spaces and punctuation tolerant)
  const postOfficeBoxRegex = /POST[\s.]*OFFICE[\s.]*BOX/i;
  // Matches: PMB / P.M.B / P M B (spaces and punctuation tolerant)
  const pmbRegex = /P\s*\.?\s*M\s*\.?\s*B/i;

  return (
    poBoxRegex.test(text) ||
    postOfficeBoxRegex.test(text) ||
    pmbRegex.test(text)
  );
};

/**
 * Format Stripe-style address fields by country postal conventions.
 * Returns a single-line string suitable for list / dropdown display.
 */
export const formatAddressLine = (info: any) => {
  if (!info) return '';

  const country = String(info.country || '').toUpperCase();
  const line1 = info.line1?.trim();
  const line2 = info.line2?.trim();
  const city = info.city?.trim();
  const state = info.state?.trim();
  const postal = info.postal_code?.trim();
  const rfc = info.rfcValue?.trim();
  const countryLabel = NationsLab?.[country] || country;

  const street = [line1, line2].filter(Boolean);
  let locality = '';

  switch (country) {
    // US / CA / AU / NZ: City, ST ZIP
    case 'US':
    case 'CA':
    case 'AU':
    case 'NZ':
    case 'PH': {
      const stateZip = [state, postal].filter(Boolean).join(' ');
      locality = [city, stateZip].filter(Boolean).join(', ');
      break;
    }
    // UK / IE: City, POSTAL
    case 'GB':
    case 'IE':
      locality = [city, postal].filter(Boolean).join(', ');
      break;
    // Most of Europe: ZIP City
    case 'DE':
    case 'AT':
    case 'CH':
    case 'NL':
    case 'BE':
    case 'FR':
    case 'IT':
    case 'ES':
    case 'PT':
    case 'PL':
    case 'SE':
    case 'NO':
    case 'DK':
    case 'FI':
    case 'CZ':
    case 'HU':
    case 'RO':
      locality = [[postal, city].filter(Boolean).join(' '), state]
        .filter(Boolean)
        .join(', ');
      break;
    // Brazil: City - State, ZIP
    case 'BR':
      locality = [[city, state].filter(Boolean).join(' - '), postal]
        .filter(Boolean)
        .join(', ');
      break;
    // Mexico: ZIP City, State
    case 'MX':
      locality = [[postal, city].filter(Boolean).join(' '), state]
        .filter(Boolean)
        .join(', ');
      break;
    // Japan: Prefecture City Postal (〒)
    case 'JP':
      locality = [state, city, postal ? `〒${postal}` : '']
        .filter(Boolean)
        .join(' ');
      break;
    // East Asia: State City Postal
    case 'CN':
    case 'KR':
    case 'TW':
    case 'HK':
    case 'SG':
      locality = [state, city, postal].filter(Boolean).join(' ');
      break;
    default:
      locality = [city, state, postal].filter(Boolean).join(', ');
  }

  let parts = [...street, locality, countryLabel].filter(Boolean);
  if (country === 'MX' && rfc) {
    parts.push(`RFC ${rfc}`);
  }
  if (country === 'CN' || country === 'TW' || country === 'HK') {
    const newCountryLabel =
      country === 'HK' ? '中国香港' : country === 'TW' ? '中国台湾' : '中国';
    parts = [newCountryLabel, state, city, ...street, postal].filter(Boolean);
  }

  return parts.join(', ');
};

export const getCountryFlag = (country?: string) => {
  if (!country || country.length !== 2) return null;
  const code = country.toUpperCase();
  return String.fromCodePoint(
    127397 + code.charCodeAt(0),
    127397 + code.charCodeAt(1)
  );
};
